import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  InfoIcon,
  PlusIcon,
  XIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import {
  IPermitTypeField,
  IWorkflowStep,
  PermitFieldType,
  WorkflowStepType,
} from '~/client/graph'
import ResourceFieldConfigurator from '~/client/src/desktop/components/ResourceFieldConfigurator'
import StruxhubBareEditableInput from '~/client/src/desktop/components/StruxhubDesktopInputs/StruxhubBareEditableInput/StruxhubBareEditableInput'
import formFieldsCaptionMap from '~/client/src/desktop/constants/formFieldsCaptionMap'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import ResourceRestriction from '~/client/src/desktop/views/ProjectSetUp/components/PermitsSetUp/components/FieldConfigurator/components/WorkflowFieldRestriction/variants/ResourceRestriction'
import { Button } from '~/client/src/shared/components/Button/Button'
import { AddIcon } from '~/client/src/shared/components/icons/AddIcon'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import {
  allNotificationPermitFieldTypes,
  equipmentPermitFieldTypes,
  isLinkedWorkflowLikeField,
  locationOnlyPermitFieldTypes,
  materialResourcePermitFieldTypes,
  multiplePermitFieldTypes,
  nonHiddeablePermitFieldTypes,
  selectOptionsPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { getResourceTypeLabels } from '~/client/src/shared/constants/resourceTypeLabels'
import Keys from '~/client/src/shared/enums/Keys'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import { useResourceCardSchema } from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { getResourceTypeIdForFieldType } from '~/client/src/shared/utils/permitFieldToResourceType'
import { areArraysEqualUnordered } from '~/client/src/shared/utils/util'

import Checklist from '../Checklist/Checklist'
import FieldStateSelector from '../FieldStateSelector'
import { DisabledResourceIdsContext } from '../FormTypeConfigurator/DisabledResourceIdsContext'
import SectionHeader from '../SectionHeader'
import DynamicFilters from './components/DynamicFilters/DynamicFilters'
import EditableTextField from './components/EditableTextField/EditableTextField'
import InlineMediaConfigurator from './components/InlineMediaConfigurator/InlineMediaConfigurator'
import NotificationStepPicker from './components/NotificationStepPicker'
import SetDefaultsSection from './components/SetDefaultsSection'
import AllowedForms from './components/WorkflowFieldRestriction/variants/AllowedForms'
import LinkedWorkflowRestriction from './components/WorkflowFieldRestriction/variants/LinkedWorkflowRestriction'
import FieldConfiguratorStore, {
  FieldConfiguratorTab,
  FieldState,
} from './FieldConfigurator.store'

// Bookability is the only blanket exclusion the configurator applies to a resource list -
// workflow-type scoping lives in each field's own allow-list, which IS the list being edited.
const isBookableRecord = (record: IResourceRecord): boolean =>
  !!record.root?.isBookable

interface IProps {
  field: IPermitTypeField
  onChange: (updatedField: IPermitTypeField) => void

  isMultipleToggleHidden?: boolean
  isCollapsed?: boolean
  isSectionExpanded?: boolean
  isLBSToggleShown?: boolean
  filterFieldsMap?: Map<string, IPermitTypeField>
  availableFilterFieldsOptions?: IFormFilterFieldOption[]
  isDynamicFilterShown?: boolean
  allWorkflowSteps?: IWorkflowStep[]

  onCollapseToggle?(): void
  onSectionToggle?(): void
  onNotificationFieldStepsChange?(fieldId: string, stepIds: string[]): void

  tableConfiguratorRenderer?(field: IPermitTypeField): JSX.Element
  materialSubFieldsRenderer?(field: IPermitTypeField): JSX.Element
  dependentFieldsRenderer?(field: IPermitTypeField): JSX.Element

  permitTypesStore?: WorkflowTypesStore
  state?: InitialState
  fileUploadingStore?: FileUploadingStore
}

const ICON_SIZE = 18
const OVERSCAN_ROW_COUNT = 6
const SELECT_OPTION_ROW_HEIGHT = 42
const SELECT_OPTIONS_MAX_HEIGHT = 200
const SELECT_OPTIONS_PADDING = 10

const _multiple = 'Multiple'
const _optionsToSelect = 'Options to select'
const _thereAreNoOptions =
  'There are no options, please add them using the input below'
const _addNewOption = 'Add a new option'
type RecordsProviderProps = {
  projectId: string
  resourceTypeId: string
  children: (
    records: IResourceRecord[],
    properties: IResourceProperty[],
    propertiesReady: boolean,
    schema: ResourceSchema,
    controlCommands: IResourceControlCommands,
  ) => JSX.Element
}

const createRecordsProvider = (
  useRecords: (config: { projectId: string }) => {
    records: IResourceRecord[]
    schema: ResourceSchema
    controlCommands: IResourceControlCommands
  },
): React.FC<RecordsProviderProps> => {
  const Provider: React.FC<RecordsProviderProps> = ({
    projectId,
    resourceTypeId,
    children,
  }) => {
    const { records, schema, controlCommands } = useRecords({ projectId })
    const runtimeContext = useResourceRuntimeContext()
    const { properties, propertiesReady } = useResourceCardSchema({
      resourceTypeId,
      runtimeContext,
      schema,
    })
    return children(
      records,
      properties,
      propertiesReady,
      schema,
      controlCommands,
    )
  }
  return observer(Provider)
}

const EquipmentRecordsProvider = createRecordsProvider(useEquipmentResources)
const LocationRecordsProvider = createRecordsProvider(useLocationResources)
const MaterialResourceRecordsProvider =
  createRecordsProvider(useMaterialResources)

const MaterialRecordsWithDeletedProvider: React.FC<{
  projectId: string
  children: (records: IResourceRecord[]) => JSX.Element
}> = observer(({ projectId, children }) =>
  children(useMaterialResources({ projectId }).recordsWithDeletedItems),
)

@inject('permitTypesStore', 'state', 'fileUploadingStore')
@observer
export default class FieldConfigurator extends React.Component<IProps> {
  private readonly store: FieldConfiguratorStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new FieldConfiguratorStore(props.field, props.onChange)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { field, onChange } = this.props
    if (field !== prevProps.field) {
      this.store.setField(field)
    }
    if (onChange !== prevProps.onChange) {
      this.store.setOnChangeHandler(onChange)
    }
  }

  public render() {
    const { field, fieldState, updateCaption } = this.store

    if (
      field.type === PermitFieldType.Section ||
      field.type === PermitFieldType.Subsection
    ) {
      return this.sectionOrSubsectionField
    }

    if (field.type === PermitFieldType.Divider) {
      return this.dividerField
    }

    if (field.type === PermitFieldType.Text) {
      return this.textField
    }

    if (field.checklist) {
      return (
        <>
          {this.checklistField}
          {this.props.dependentFieldsRenderer?.(field)}
        </>
      )
    }

    const { isCollapsed, onCollapseToggle } = this.props

    const inlineMediaContent = this.inlineMediaContent
    const hasConfigContent =
      !!this.notificationSettings ||
      !!this.materialResourceFieldConfigurator ||
      !!this.resourceFieldConfigurator ||
      !!this.locationResourceFieldConfigurator ||
      !!this.linkedWorkflowToggles ||
      !!this.preciseLocationToggle ||
      !!this.userStampToggle ||
      !!this.selectorOptions ||
      !!this.tableConfigurator ||
      !!inlineMediaContent

    const isToggleable = hasConfigContent && !!onCollapseToggle
    const useTabbedLayout = this.useTabbedLayout
    const isExpandedWithContent = isToggleable && !isCollapsed

    return (
      <div
        className={classList({
          'relative overflow-hidden tw:my-1': true,
          'tw:rounded-md tw:border tw:border-border tw:bg-background tw:pb-2':
            isExpandedWithContent,
        })}
      >
        <div
          className={classList({
            'row pl5 tw:gap-2.5 pr15 tw:py-0.5 bg-palette-brand-lightest': true,
            'ba-light-input-border brada4': !isExpandedWithContent,
            'tw:border-b tw:border-border': isExpandedWithContent,
            pointer: isToggleable,
          })}
          onClick={isToggleable ? this.handleTitleRowClick : undefined}
          role={isToggleable ? 'button' : undefined}
          aria-expanded={isToggleable ? !isCollapsed : undefined}
        >
          <ChevronRightIcon
            size={16}
            className={classList({
              'no-flex tw:transition-transform': true,
              'tw:text-muted-foreground': isToggleable,
              'tw:rotate-90': isToggleable && !isCollapsed,
              'tw:invisible': !isToggleable,
            })}
          />
          <TooltipWrapper
            content={formFieldsCaptionMap[field.type]}
            className="row no-grow"
          >
            <PermitFieldIcon fieldName={field.type} />
          </TooltipWrapper>
          <StruxhubBareEditableInput
            className="row text large bold"
            inputClassName="text large bold ba2-group-highlight brada3"
            value={field.caption}
            onChange={updateCaption}
          />
          {this.multipleToggle}
          {!nonHiddeablePermitFieldTypes.includes(field.type) && (
            <FieldStateSelector
              className={toggleClass(
                'not-allowed',
                !field.canEditIsMandatory && !field.canEditIsShown,
              )}
              isDisabled={!field.canEditIsMandatory && !field.canEditIsShown}
              fieldState={fieldState}
              onChange={this.handleSelectStateChange}
            />
          )}
        </div>
        {this.materialFieldNote}
        {!isCollapsed && useTabbedLayout && this.tabbedContent}
        {!isCollapsed && !useTabbedLayout && (
          <>
            {this.notificationSettings}
            {this.preciseLocationToggle}
            {this.userStampToggle}
            {this.selectorOptions}
            {this.tableConfigurator}
            {inlineMediaContent}
            {this.props.dependentFieldsRenderer?.(this.store.field)}
          </>
        )}
      </div>
    )
  }

  private get useTabbedLayout(): boolean {
    const { field } = this.store
    return (
      isLinkedWorkflowLikeField(field.type) ||
      equipmentPermitFieldTypes.includes(field.type) ||
      materialResourcePermitFieldTypes.includes(field.type) ||
      locationOnlyPermitFieldTypes.includes(field.type)
    )
  }

  private get visibleTabs(): FieldConfiguratorTab[] {
    const { field } = this.store
    const {
      isDynamicFilterShown,
      materialSubFieldsRenderer,
      dependentFieldsRenderer,
    } = this.props
    const isLinkedWorkflow = isLinkedWorkflowLikeField(field.type)
    const isEquipment = equipmentPermitFieldTypes.includes(field.type)
    const isMaterialResource = materialResourcePermitFieldTypes.includes(
      field.type,
    )
    const isLocation = locationOnlyPermitFieldTypes.includes(field.type)

    const tabs: FieldConfiguratorTab[] = [
      FieldConfiguratorTab.SelectionSettings,
    ]

    if (isMaterialResource && materialSubFieldsRenderer) {
      tabs.push(FieldConfiguratorTab.AssociatedFields)
    }

    if (
      isDynamicFilterShown &&
      (isEquipment || isMaterialResource || isLocation) &&
      !isLinkedWorkflow
    ) {
      tabs.push(FieldConfiguratorTab.DynamicFilters)
    }

    if (
      dependentFieldsRenderer &&
      (isEquipment || isMaterialResource || isLocation)
    ) {
      tabs.push(FieldConfiguratorTab.DependentFields)
    }

    return tabs
  }

  private get tabbedContent(): JSX.Element {
    const tabs = this.visibleTabs
    const activeTab = tabs.includes(this.store.activeTab)
      ? this.store.activeTab
      : FieldConfiguratorTab.SelectionSettings

    return (
      <Tabs
        value={activeTab}
        onValueChange={value =>
          this.store.setActiveTab(value as FieldConfiguratorTab)
        }
        className="tw:gap-0"
      >
        {tabs.length > 1 && (
          <div className="tw:border-b tw:border-border tw:mx-4 tw:pt-3">
            <TabsList variant="line">
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  disabled={this.isTabDisabled(tab)}
                >
                  {this.getTabLabel(tab)}
                  <TooltipWrapper content={this.getTabTooltip(tab)}>
                    <InfoIcon className="tw:size-4 tw:text-muted-foreground" />
                  </TooltipWrapper>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        )}
        {tabs.map(tab => (
          <TabsContent key={tab} value={tab}>
            {this.renderTabContent(tab)}
          </TabsContent>
        ))}
      </Tabs>
    )
  }

  private getTabLabel(tab: FieldConfiguratorTab): string {
    switch (tab) {
      case FieldConfiguratorTab.SelectionSettings:
        return Localization.translator.selection
      case FieldConfiguratorTab.DynamicFilters:
        return Localization.translator.conditionalFilters
      case FieldConfiguratorTab.DependentFields:
        return Localization.translator.conditionalFields
      case FieldConfiguratorTab.AssociatedFields:
        return Localization.translator.associatedFields
      case FieldConfiguratorTab.CardView:
        return Localization.translator.cardView
    }
  }

  private isTabDisabled(tab: FieldConfiguratorTab): boolean {
    if (tab !== FieldConfiguratorTab.DependentFields) {
      return false
    }
    return this.store.field.isMultiple
  }

  private getTabTooltip(tab: FieldConfiguratorTab): string {
    switch (tab) {
      case FieldConfiguratorTab.SelectionSettings:
        return Localization.translator.selectionTooltip
      case FieldConfiguratorTab.AssociatedFields:
        return Localization.translator.itemFieldsTooltip
      case FieldConfiguratorTab.DynamicFilters:
        return Localization.translator.conditionalFiltersTooltip
      case FieldConfiguratorTab.DependentFields:
        return Localization.translator.conditionalFieldsTooltip
      default:
        return ''
    }
  }

  private renderTabContent(tab: FieldConfiguratorTab): JSX.Element {
    switch (tab) {
      case FieldConfiguratorTab.SelectionSettings:
        return (
          <>
            {this.linkedWorkflowToggles}
            {this.resourceFieldConfigurator}
            {this.materialResourceFieldConfigurator}
            {this.locationResourceFieldConfigurator}
          </>
        )
      case FieldConfiguratorTab.DynamicFilters:
        return this.dynamicFiltersContent
      case FieldConfiguratorTab.DependentFields:
        return this.props.dependentFieldsRenderer?.(this.store.field) ?? null
      case FieldConfiguratorTab.AssociatedFields:
        return this.props.materialSubFieldsRenderer?.(this.store.field) ?? null
      case FieldConfiguratorTab.CardView:
        return null
    }
  }

  private handleTitleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement
    if (
      target.closest(
        'input, select, textarea, button, [role="combobox"], [role="switch"]',
      )
    ) {
      return
    }
    this.props.onCollapseToggle?.()
  }

  private get sectionOrSubsectionField(): JSX.Element {
    const { field, updateCaption } = this.store

    const { isSectionExpanded, onSectionToggle, isCollapsed } = this.props

    const isSubsection = field.type === PermitFieldType.Subsection
    const isExpanded = onSectionToggle ? isSectionExpanded : !isCollapsed
    const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon

    return (
      <SectionHeader
        header={field.caption}
        className={classList({
          'text small-header subsection-field h32': isSubsection,
          'tw:min-h-9': !isSubsection,
        })}
        isSubHeader={true}
        isEditable={true}
        onEditChange={updateCaption}
        iconElement={
          <ChevronIcon
            size={ICON_SIZE}
            className="no-grow mr15 pointer"
            onClick={this.onCollapseClick}
          />
        }
      />
    )
  }

  private get dividerField(): JSX.Element {
    const { field, updateCaption } = this.store

    return (
      <div className="row py20 overflow-hidden">
        <hr className="ba-none bt-light-input-border min-width30" />
        <StruxhubBareEditableInput
          className="row text small light no-white-space-wrap bold lp15 uppercase w-fit-content mx10 ellipsis flex-grow3"
          inputClassName="text small light bold lp15 uppercase mx10 ba2-group-highlight brada3"
          value={field.caption}
          onChange={updateCaption}
        />
        <hr className="ba-none bt-light-input-border min-width30" />
      </div>
    )
  }

  private get textField(): JSX.Element {
    const { field, updateCaption } = this.store

    return (
      <div className="row px15 py12 ba-dashed-palette-grey brada8">
        <EditableTextField
          className="text large"
          value={field.caption}
          onChange={updateCaption}
        />
      </div>
    )
  }

  private get inlineMediaContent(): JSX.Element {
    const { field } = this.store
    if (field.type !== PermitFieldType.InlineMedia) {
      return null
    }

    const projectId = this.props.state?.activeProject?.id
    return (
      <InlineMediaConfigurator
        field={field}
        onChange={this.props.onChange}
        fileUploadingStore={this.props.fileUploadingStore}
        projectId={projectId}
      />
    )
  }

  private get checklistField(): JSX.Element {
    const { onChange } = this.props
    const { field, updateChecklist } = this.store
    const isQuestion = field.type === PermitFieldType.Question
    const checklistClassName = isQuestion ? 'my12' : 'mb12'

    return (
      <Checklist
        className={checklistClassName}
        field={field}
        isSingleMode={isQuestion}
        onChange={updateChecklist}
        onFieldChange={onChange}
      />
    )
  }

  private get selectorOptions(): JSX.Element {
    const {
      field,
      isAddSelectOptionShown,
      newSelectOption,
      addNewSelectOption,
    } = this.store

    if (!selectOptionsPermitFieldTypes.includes(field.type)) {
      return null
    }

    const selectOptions = field?.selectOptions || []

    return (
      <div className="col mt15 ml20">
        <span className="text large grey py10">{_optionsToSelect}</span>
        {!selectOptions.length && (
          <span className="text grey py10">{_thereAreNoOptions}</span>
        )}
        {!!selectOptions.length && (
          <div
            className="select-options-container ba-dashed-palette-grey brada8 my10"
            style={{
              padding: SELECT_OPTIONS_PADDING,
              maxHeight: SELECT_OPTIONS_MAX_HEIGHT + SELECT_OPTIONS_PADDING * 2,
            }}
          >
            <AutoSizer disableHeight={true}>
              {({ width }) => (
                <List
                  data={selectOptions}
                  width={width}
                  height={Math.min(
                    SELECT_OPTIONS_MAX_HEIGHT,
                    SELECT_OPTION_ROW_HEIGHT * selectOptions.length,
                  )}
                  rowHeight={SELECT_OPTION_ROW_HEIGHT}
                  rowCount={selectOptions.length}
                  rowRenderer={this.renderSelectOption}
                  scrollToAlignment="start"
                  overscanRowCount={OVERSCAN_ROW_COUNT}
                />
              )}
            </AutoSizer>
          </div>
        )}
        {isAddSelectOptionShown && (
          <div className="row y-center mw270">
            <StruxhubInput
              label={_addNewOption}
              isRequiredTextHidden={true}
              isHelperTextCondensed
              value={newSelectOption}
              onChange={this.handleSelectOptionChange}
              onValueReset={this.handleResetSelectOptionChange}
              onKeyDown={this.handleKeyPress}
            />
            <Button
              variant="primary-ghost"
              size="icon"
              className="tw:mt-4 tw:ml-2.5 tw:flex-none!"
              aria-label={Localization.translator.add}
              onClick={addNewSelectOption}
            >
              <PlusIcon />
            </Button>
          </div>
        )}
      </div>
    )
  }

  private renderSelectOption = ({ key, style, index }: any): JSX.Element => {
    const props = { key, style }
    const { field, removeSelectOption } = this.store
    const option = (field.selectOptions || [])[index]

    return (
      <div
        {...props}
        className={classList({
          'row py10': true,
          'bt-light-input-border': !!index,
        })}
      >
        <span className="text grey-light extra-large ellipsis" title={option}>
          {option}
        </span>
        <XIcon
          size={16}
          className="bg-grey-scale-light br-rounded pointer text white mr20"
          onClick={removeSelectOption.bind(null, index)}
        />
      </div>
    )
  }

  private get multipleToggle(): JSX.Element {
    const { field, toggleIsMultipleValue } = this.store

    if (
      this.props.isMultipleToggleHidden ||
      !multiplePermitFieldTypes.includes(field.type)
    ) {
      return null
    }

    return (
      <div
        className={classList({
          'tw:flex tw:items-center tw:gap-2 tw:pl-1.5 tw:ml-1 tw:mr-3 tw:flex-none!': true,
          'not-allowed': this.store.isMultipleDisabled,
        })}
      >
        <div className="text large bold tw:whitespace-nowrap">{_multiple}</div>
        <Switch
          disabled={this.store.isMultipleDisabled}
          checked={field.isMultiple || false}
          onCheckedChange={toggleIsMultipleValue}
        />
      </div>
    )
  }

  private get tableConfigurator(): JSX.Element {
    const { field, tableConfiguratorRenderer } = this.props

    if (field.type !== PermitFieldType.Table) {
      return null
    }

    return tableConfiguratorRenderer?.(field) ?? null
  }

  private get materialFieldNote(): JSX.Element {
    const { field } = this.props
    if (!field.isUsedInMaterial) {
      return null
    }

    let note = ''
    switch (field.type) {
      case PermitFieldType.InputNumber:
        note = Localization.translator.usedInMaterialsTableCalculations
        break
      case PermitFieldType.LocationInformational:
        note =
          Localization.translator.usedToSpecifySourceLocationInMaterialsTable
        break
    }

    return <p className="text large light">{note}</p>
  }

  private get notificationSettings(): JSX.Element {
    const { field } = this.store
    const { allWorkflowSteps, onNotificationFieldStepsChange } = this.props

    if (
      !allWorkflowSteps ||
      !onNotificationFieldStepsChange ||
      !allNotificationPermitFieldTypes.includes(field.type)
    ) {
      return null
    }

    const nonRecurringSteps = allWorkflowSteps.filter(
      s => s.type !== WorkflowStepType.RecurringInspection,
    )

    const selectedStepIds = nonRecurringSteps
      .filter(s => s.notificationFieldIds?.includes(field.id))
      .map(s => s.id)

    const isEnabled = selectedStepIds.length > 0

    return (
      <div className="tw:px-8 tw:py-3 tw:space-y-2">
        <div className="tw:flex tw:items-center tw:gap-2">
          <Switch
            checked={isEnabled}
            onCheckedChange={checked => {
              const stepIds = checked ? nonRecurringSteps.map(s => s.id) : []
              onNotificationFieldStepsChange(field.id, stepIds)
            }}
          />
          <span className="tw:text-body-md">
            {Localization.translator.allowNotificationsFor(field.caption)}
          </span>
        </div>
        {isEnabled && (
          <div className="tw:space-y-1">
            <span className="tw:text-body-md tw:text-muted-foreground">
              {Localization.translator.notifyFor}:
            </span>
            <NotificationStepPicker
              allWorkflowSteps={allWorkflowSteps}
              selectedStepIds={selectedStepIds}
              onApply={stepIds =>
                onNotificationFieldStepsChange(field.id, stepIds)
              }
            />
          </div>
        )}
      </div>
    )
  }

  private get preciseLocationToggle(): JSX.Element {
    const { field, toggleAllowPreciseLocation } = this.store

    if (PermitFieldType.Location !== field.type) {
      return null
    }

    // Same markup as ResourceFieldConfigurator's FieldToggle - switch first, then the
    // label - so it reads as one list with the two Allow Users toggles it now sits above.
    return (
      <div className="tw:h-10 tw:pr-4 tw:py-2.5 tw:inline-flex tw:justify-start tw:items-center tw:gap-3">
        <Switch
          checked={field.allowPreciseLocation || false}
          onCheckedChange={toggleAllowPreciseLocation}
        />
        <span className="tw:text-label-lg tw:text-foreground">
          {Localization.translator.preciseLocation}
        </span>
      </div>
    )
  }

  private get userStampToggle(): JSX.Element {
    const { field, toggleUserStampEnabled } = this.store

    if (PermitFieldType.Signature !== field.type) {
      return null
    }

    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:ml-5 tw:pt-4">
        <div className="text large bold tw:whitespace-nowrap">
          {Localization.translator.userStamp}
        </div>
        <TooltipWrapper
          content={Localization.translator.userStampTooltip}
          className="tw:shrink-0 tw:mr-1"
        >
          <InfoIcon size={16} className="tw:block tw:text-muted-foreground" />
        </TooltipWrapper>
        <Switch
          checked={field.isUserStampEnabled || false}
          onCheckedChange={toggleUserStampEnabled}
        />
      </div>
    )
  }

  private get linkedWorkflowToggles(): JSX.Element {
    const { field, toggleAllowSelectExisting, toggleAllowCreateNew } =
      this.store

    if (!isLinkedWorkflowLikeField(field.type)) {
      return null
    }

    const { permitTypesStore } = this.props
    const { allowedWorkflowTypes } = this.store
    const resolvedTypes = allowedWorkflowTypes
      .map(type => permitTypesStore.getActualTypeByType(type))
      .filter(Boolean)
    const selectedWorkflowTypes = !resolvedTypes.length
      ? permitTypesStore.actualTypes
      : resolvedTypes

    const isSelectExistingEnabled = field.allowSelectExisting ?? true

    return (
      <>
        <div className="tw:flex tw:items-center tw:gap-3 tw:pt-5 tw:pl-5">
          <Switch
            checked={field.allowCreateNew ?? true}
            onCheckedChange={toggleAllowCreateNew}
          />
          <span className="tw:text-label-lg tw:text-foreground">
            {Localization.translator.allowUsersToCreateNewWorkflow}
          </span>
        </div>
        <div className="tw:flex tw:items-center tw:gap-3 tw:pt-5 tw:pl-5">
          <Switch
            checked={isSelectExistingEnabled}
            onCheckedChange={toggleAllowSelectExisting}
          />
          <span className="tw:text-label-lg tw:text-foreground">
            {Localization.translator.allowUsersToSelectExistingWorkflows}
          </span>
        </div>
        {isSelectExistingEnabled && (
          <>
            <SetDefaultsSection field={field} onChange={this.props.onChange} />
            {(() => {
              const isActivityField =
                field.type === PermitFieldType.Activity ||
                field.type === PermitFieldType.PrimaryActivity
              return (
                <div
                  className={classList({
                    'tw:opacity-50 tw:pointer-events-none': isActivityField,
                  })}
                >
                  <div className="tw:flex tw:items-center tw:gap-1.5 ml20 pt20">
                    <span className="text large medium-bold grey">
                      {Localization.translator.allowToSelect}:
                    </span>
                    {isActivityField && (
                      <TooltipWrapper
                        content={
                          Localization.translator.activityFieldRestrictedTooltip
                        }
                      >
                        <InfoIcon className="tw:size-4 tw:text-muted-foreground tw:pointer-events-auto" />
                      </TooltipWrapper>
                    )}
                  </div>
                  <div className="row ml20 pt5 mw300">
                    <LinkedWorkflowRestriction
                      selectedTypes={selectedWorkflowTypes}
                      allTypes={permitTypesStore.actualTypes}
                      onSelectionChange={this.handleWorkflowTypesChange}
                    />
                  </div>
                </div>
              )
            })()}
          </>
        )}
      </>
    )
  }

  // Hides resources that aren't bookable or aren't allowed for the workflow
  // type being configured. Membership is version-aware, so the active type's
  // raw id is normalized to its actual-type id first.
  private renderResourceConfigurator(
    fieldTypes: PermitFieldType[],
    RecordsProvider: React.FC<RecordsProviderProps>,
    resourceLabel: string,
    applyWorkflowTypeVisibility = false,
    leadingContent?: JSX.Element,
  ): JSX.Element {
    const field = this.store.field
    if (!fieldTypes.includes(field.type)) {
      return null
    }

    const resourceTypeId = getResourceTypeIdForFieldType(field.type)
    if (!resourceTypeId) return null

    const projectId = this.props.state?.activeProject?.id

    // Read every prop this method needs BEFORE the JSX. Everything below the
    // return is inside RecordsProvider's observer render, where a props access
    // throws.
    const { onChange } = this.props

    const {
      updateRestrictedIdsFilters,
      allowedTemplateIds,
      updateAllowedTemplateIds,
    } = this.store

    return (
      <RecordsProvider projectId={projectId} resourceTypeId={resourceTypeId}>
        {(records, properties, propertiesReady, schema, controlCommands) => (
          <ResourceFieldConfigurator
            key={field.id}
            field={field}
            resourceTypeId={resourceTypeId}
            onChange={onChange}
            records={records}
            properties={properties}
            propertiesReady={propertiesReady}
          >
            <div className="tw:pt-2.5">
              {leadingContent}
              <ResourceFieldConfigurator.FieldToggle
                property="allowCreateNew"
                label={Localization.translator.allowUsersToCreateNew(
                  resourceLabel,
                )}
              />
            </div>
            <ResourceFieldConfigurator.FieldToggle
              property="allowSelectExisting"
              label={Localization.translator.allowUsersToSelectExisting(
                resourceLabel,
              )}
            />
            {(field.allowCreateNew ?? true) && (
              <div className="tw:flex-col tw:py-3 tw:max-w-[330px]">
                <ResourceFieldConfigurator.SectionLabel
                  text={getResourceTypeLabels(
                    resourceTypeId,
                  ).creationFormLabel()}
                />
                <AllowedForms
                  schema={schema}
                  resourceTypeId={resourceTypeId}
                  allowedTemplateIds={allowedTemplateIds}
                  onChange={updateAllowedTemplateIds}
                />
              </div>
            )}
            {(field.allowSelectExisting ?? true) && (
              <>
                <div className="tw:py-3">
                  <ResourceFieldConfigurator.DefaultsSection />
                </div>
                <div className="tw:flex-col tw:py-3 tw:max-w-[330px]">
                  <ResourceFieldConfigurator.SectionLabel
                    text={Localization.translator.allowToSelect}
                  />
                  <ResourceRestriction
                    schema={schema}
                    resourceTypeId={resourceTypeId}
                    resourceLabel={resourceLabel}
                    records={records}
                    fieldFilterOptions={field.filterOptions}
                    onChange={updateRestrictedIdsFilters}
                    projectId={projectId}
                    controlCommands={controlCommands}
                    // This list IS the field's own allow-list, so the only blanket
                    // exclusion left is a resource that cannot be booked at all.
                    recordPredicate={
                      applyWorkflowTypeVisibility ? isBookableRecord : undefined
                    }
                  />
                </div>
              </>
            )}
          </ResourceFieldConfigurator>
        )}
      </RecordsProvider>
    )
  }

  private get resourceFieldConfigurator(): JSX.Element {
    return this.renderResourceConfigurator(
      equipmentPermitFieldTypes,
      EquipmentRecordsProvider,
      Localization.translator.equipment,
      true,
    )
  }

  private get materialResourceFieldConfigurator(): JSX.Element {
    return this.renderResourceConfigurator(
      materialResourcePermitFieldTypes,
      MaterialResourceRecordsProvider,
      Localization.translator.materials,
      true,
    )
  }

  /**
   * The controls a Location field showed before it moved onto the tabbed layout, and that
   * the tabbed layout would otherwise drop on the floor - Precise location is Location-only
   * and has nowhere else to live.
   *
   * Each getter already returns null for the types it does not apply to, so the Location
   * guard is what keeps this from changing equipment and material fields, which reached
   * the tabbed layout without ever rendering these.
   */
  private get locationOnlyFlatControls(): JSX.Element {
    if (!locationOnlyPermitFieldTypes.includes(this.store.field.type)) {
      return null
    }

    // A block wrapper, not a fragment: FieldToggle's root is inline-flex, so bare siblings
    // would share a line with the Allow Users toggle this sits directly above.
    return (
      <div className="tw:flex tw:flex-col">
        {this.preciseLocationToggle}
        {this.lbsLogicToggle}
      </div>
    )
  }

  /**
   * The only control for "Filter by LBS". The flag it writes is still very much alive - FormConfigurator seeds it true
   * on every conditional location sub-field, and FormLocationResourcePicker narrows that sub-field's
   * options to the parent location's descendants because of it - so without this the setting
   * would be permanently on and unreachable.
   */
  private get lbsLogicToggle(): JSX.Element {
    const { field, updateLBSLogic } = this.store

    if (!this.props.isLBSToggleShown) {
      return null
    }

    return (
      <div className="tw:h-10 tw:pr-4 tw:py-2.5 tw:inline-flex tw:justify-start tw:items-center tw:gap-3">
        <Switch
          checked={field.isLBSLogicEnabled}
          onCheckedChange={updateLBSLogic}
        />
        <span className="tw:text-label-lg tw:text-foreground">
          {Localization.translator.filterByLBS}
        </span>
      </div>
    )
  }

  /**
   * Locations are a resource type like equipment and materials (STX1-597), so the
   * Location field configures the same way: Default Filters for the kind, "Allow to
   * select" for the individual locations. The bespoke Filter type / Filter options pair
   * it used to render is gone.
   *
   * The kind is a DEFAULT, not a restriction - the same thing equipment's type facet is.
   * Picking one pre-selects the picker's filter and the user can clear it; nothing writes a
   * hard filterOptions entry, and the migration moves the ones that already exist across.
   */
  private get locationResourceFieldConfigurator(): JSX.Element {
    return this.renderResourceConfigurator(
      locationOnlyPermitFieldTypes,
      LocationRecordsProvider,
      Localization.translator.location,
      true,
      this.locationOnlyFlatControls,
    )
  }

  private get dynamicFiltersContent(): JSX.Element {
    const { field } = this.store
    const isEquipment = equipmentPermitFieldTypes.includes(field.type)
    const isMaterialResource = materialResourcePermitFieldTypes.includes(
      field.type,
    )

    const isLocation = locationOnlyPermitFieldTypes.includes(field.type)

    if (
      !this.props.isDynamicFilterShown ||
      (!isEquipment && !isMaterialResource && !isLocation)
    ) {
      return null
    }

    const {
      restrictedLocationTypes,
      restrictedLocIdsForDynamicFilters,
      existingDynamicFilters,
      updateDynamicFilters,
    } = this.store

    const projectId = this.props.state?.activeProject?.id

    return (
      <div className="tw:pl-6">
        <MaterialRecordsWithDeletedProvider projectId={projectId}>
          {records => (
            <DisabledResourceIdsContext.Consumer>
              {disabledResourceIds => (
                <DynamicFilters
                  fieldId={field.id}
                  filters={existingDynamicFilters}
                  restrictedLocationIds={[
                    ...restrictedLocIdsForDynamicFilters,
                    ...disabledResourceIds,
                  ]}
                  restrictedLocationTypes={restrictedLocationTypes}
                  filterFieldsOptions={this.props.availableFilterFieldsOptions}
                  filterFieldsMap={this.props.filterFieldsMap}
                  materialResourceRecords={records}
                  isConfiguredFieldMaterialResource={isMaterialResource}
                  configuredFieldType={field.type}
                  disabledResourceIds={disabledResourceIds}
                  onChange={updateDynamicFilters}
                  renderNewRuleButton={onClick => (
                    <div>
                      <Button
                        variant="ghost"
                        onClick={onClick}
                        className="tw:w-fit tw:text-muted-foreground"
                        size="lg"
                      >
                        <AddIcon data-icon="inline-start" size="xs" />
                        {Localization.translator.addConditionalFilter}
                      </Button>
                    </div>
                  )}
                  renderHeader={(
                    enableCreationMode,
                    { filtersCount, isExpanded, onToggle },
                  ) => (
                    <ResourceFieldConfigurator.CollapsibleSectionHeader
                      count={filtersCount}
                      isExpanded={isExpanded}
                      onToggle={onToggle}
                      onCreateNew={enableCreationMode}
                      countLabel={n =>
                        Localization.translator.xConditionalFilters(n)
                      }
                      sectionLabel={Localization.translator.conditionalFilters}
                      addButtonLabel={
                        Localization.translator.addConditionalFilter
                      }
                    />
                  )}
                />
              )}
            </DisabledResourceIdsContext.Consumer>
          )}
        </MaterialRecordsWithDeletedProvider>
      </div>
    )
  }

  private handleWorkflowTypesChange = (selectedTypes: string[]) => {
    const { permitTypesStore } = this.props
    const { updateAllowedWorkflowTypes } = this.store

    const newSelectedTypes = selectedTypes
      .map(type => permitTypesStore.getActualTypeByType(type))
      .filter(Boolean)
      .map(type => type.type)

    const allTypes = permitTypesStore.actualTypes.map(pt => pt.type)

    if (areArraysEqualUnordered(newSelectedTypes, allTypes)) {
      updateAllowedWorkflowTypes([])
    } else {
      updateAllowedWorkflowTypes(newSelectedTypes)
    }
  }

  private handleSelectStateChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.store.changeFieldState(event.target.value as FieldState)
  }

  private handleSelectOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.store.updateSelectOption(event.target.value)
  }

  private handleResetSelectOptionChange = () => {
    this.store.updateSelectOption('')
  }

  private handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === Keys.Enter && !event.shiftKey) {
      this.store.addNewSelectOption()
    }
  }

  private onCollapseClick = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    const { onSectionToggle, onCollapseToggle } = this.props
    ;(onSectionToggle ?? onCollapseToggle)?.()
  }
}
